export const toNumber = (value, fallback = 0) => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

export const formatCurrency = (amount) => `Rs ${toNumber(amount).toFixed(2)}`;

// ── Per-item helpers ─────────────────────────────────────────────────────────

export const getCartItemSubtotal = (item) =>
  toNumber(item?.price) * toNumber(item?.quantity, 1);

export const getCartItemGstAmount = (item) =>
  (getCartItemSubtotal(item) * toNumber(item?.gst)) / 100;

/**
 * Best line total for a single item.
 * Prefers server-provided item_total; falls back to local price × qty.
 */
export const getCartItemTotal = (item) => {
  if (item?.item_total && toNumber(item.item_total) > 0) return toNumber(item.item_total);
  return getCartItemSubtotal(item);
};

// ── Cart-level totals ────────────────────────────────────────────────────────

/**
 * Returns totals using server cart_meta when available (authenticated cart),
 * otherwise falls back to local calculation (guest cart / offline).
 *
 * Returns: { subtotal, gstTotal, delivery, total,
 *            freeDeliveryMin, amountNeededForFreeDelivery, appliedPincode }
 */
export const getCartTotals = (cart = [], cart_meta = null) => {
  if (
    cart_meta &&
    (toNumber(cart_meta.total_amount) > 0 || toNumber(cart_meta.subtotal) > 0)
  ) {
    return {
      subtotal:                    toNumber(cart_meta.subtotal),
      gstTotal:                    toNumber(cart_meta.tax_amount),
      delivery:                    toNumber(cart_meta.delivery_fee),
      total:                       toNumber(cart_meta.total_amount),
      freeDeliveryMin:             toNumber(cart_meta.free_delivery_min_amount, 500),
      amountNeededForFreeDelivery: toNumber(cart_meta.amount_needed_for_free_delivery),
      appliedPincode:              cart_meta.applied_pincode ?? null,
    };
  }

  // Guest / fallback — local calculation
  const subtotal = cart.reduce((s, i) => s + getCartItemSubtotal(i), 0);
  const gstTotal = cart.reduce((s, i) => s + getCartItemGstAmount(i), 0);
  const freeDeliveryMin = 500;
  const delivery = subtotal >= freeDeliveryMin ? 0 : 40;

  return {
    subtotal,
    gstTotal,
    delivery,
    total:                       subtotal + gstTotal + delivery,
    freeDeliveryMin,
    amountNeededForFreeDelivery: Math.max(0, freeDeliveryMin - subtotal),
    appliedPincode:              null,
  };
};

export const hasGstBreakdown = (cart = []) =>
  cart.some((item) => item?.gst !== undefined && item?.gst !== null && item?.gst !== '');
